import { Fragment, useEffect, useRef, useState } from 'react';
import useCardReveal from '../../hooks/useCardReveal';
import { formatLocalMoment } from '../../utils/specimenCode';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import OrganicEmptyState from '../organic/OrganicEmptyState.jsx';
import OrganicLoader from '../organic/OrganicLoader.jsx';
import { conditionsSummary } from '../PlateConditionsFields';
import IsolateDetails from './IsolateDetails';
import IsolateLightbox from './IsolateLightbox';
import ImageSourcePicker from './ImageSourcePicker';
import LabAnalyticsTray, { pickerOffered, trayTools } from './LabAnalyticsTray';
import { ORIGINS, PAGE_SIZE } from './useIsolatesOverview';
import { checkablePlate, defaultPlateId, galleryTarget, rowActions } from './quickActions';
import { tableStates } from './stages';
import {
  Code, Day, IdentificationCell, Joined, Ltr, PipelineStrip, PlatePill, ReviewDue, SlantPill, Taxon, bankPlace,
} from './cells';
import * as S from '../specimens/styles';

/*
 * Home's master table: every isolate the reader may see, one row each,
 * under the canvas. It is there without a click; a node, a state count, a
 * unit or an origin only filters its rows (the view= and its friends), and
 * never changes what a row says -- the server sends every row with every
 * column's data, whatever the filter.
 *
 * One set of columns, each saying one thing, the ones that tell isolates
 * apart first:
 *
 *   ID             the isolate under its photograph (PhotoTile): the picture
 *                  opens large (IsolateLightbox) with every photograph of it
 *                  and a strip of them all, the code opens its dossier below
 *   Name & place   what its collector called it (given_name, 0110) and where
 *                  it was found, each on its own line; then its origin's
 *                  code and moment, an intake's donor and arrival; its strain
 *   Lab Code       the bank's number for it
 *   Taxon / ID     its name, and its latest identification as a hint
 *   Pipeline       the five stages, the one summary of where it is
 *   Culture        its live plates, the plate "Log check" opens on, and how
 *                  many plates are still to make (not yet a culture)
 *   Bank           where it is kept, its slants' state, how it is kept
 *   Actions        what its population needs next, and its own page
 *
 * Someone without lab visibility ("My isolates") sees no Culture or Bank:
 * there are no plates or slants behind them to show.
 *
 * Under 768 px the table is stacked: each row a card, each cell a line
 * labelled by its column (the td's data-label, drawn by index.css), the ID
 * cell the card's header. The thead stays in the DOM, visually hidden, so
 * the cells keep their headers for a screen reader; the explicit table roles
 * keep that structure where `display: block` would otherwise drop it
 * (Chromium and WebKit). Wider, the table scrolls sideways with the ID
 * column frozen (.hub-sticky), its photograph with it, so a laptop that
 * cannot show every column still shows the picture, the code and, next to
 * them, the name and the place.
 *
 * The toolbar's search, state chips, sort and page live in the URL (the hub
 * writes them), so a reload or a shared link opens the same table. Typing a
 * search with no order chosen also sorts by relevance (best match first);
 * clearing it drops that sort again.
 *
 * In the canvas's isolates view each row has a pipeline above it
 * (IsolatePipelines); pressing one highlights its row here (?hl=) and
 * brings it into view.
 *
 * Each row's Actions end with "Lab analytics", the toggle of its tray of
 * analysis tools (LabAnalyticsTray: Choose an image..., Thermal, Culture /
 * Petri, Spore / Microscopy, each to whoever its tool lets in; no toggle when
 * none is). One tray is open at a time (trayFor), and any new rows -- another
 * page, a filter, a sort, a refresh -- close it. Wide, it opens under the
 * row's buttons. Stacked, the card slides aside to show it at its physical
 * left, from the toggle.
 *
 * Stacked, a finger dragged left to right across a card (hooks/
 * useCardReveal.js) uncovers "Open in the workbench" at its physical left
 * and, let go far enough, opens the image source picker for that isolate
 * (ImageSourcePicker: one of its pictures, a file, or the dish camera, then
 * the analysis workbench on it) while the card slides back; right to left
 * closes a tray the toggle opened. The tray's first button opens the same
 * picker, without a swipe. Closed, the picker hands focus back to what
 * opened it: that button, or the card's toggle after a swipe. Those rows are
 * marked data-swipe="card", so the page's own swipe leaves strokes on them
 * alone.
 */

export const COLUMNS = ['isolateId', 'origin', 'labCode', 'taxon', 'pipeline', 'culture', 'bank', 'actions'];
const LAB_ONLY = ['culture', 'bank'];
/** The columns a reader is shown: without lab visibility, no Culture or Bank. */
export const columnsFor = (labScope) => (labScope === 'none' ? COLUMNS.filter((c) => !LAB_ONLY.includes(c)) : COLUMNS);
// 'relevance' ranks a search's rows best first (the hero search links with
// it, and the search box chooses it while the reader has chosen no order).
export const SORTS = ['created', 'code', 'lab_code', 'collected', 'taxon', 'relevance'];

// The Culture column's chips: plates on the bench, made and not finished. A
// plate still awaiting creation has not been poured (no culture yet: the
// server counts an isolate with only such plates as awaiting the lab), so it
// is said as a number to make, not as a chip.
const livePlates = (item) => (item.plates || []).filter(checkablePlate);
const unstartedPlates = (item) => (item.plates || []).filter((p) => p.status === 'awaiting_creation');
// The plate the culture hint describes: the one "Log check" opens on, so the
// row and its default action agree.
const shownPlate = (item) => (item.plates || []).find((p) => p.plate_id === defaultPlateId(item)) || null;
const fromIntake = (item) => item.origin_table === 'external_collections';

/** The reader's calendar day of an ISO moment, YYYY-MM-DD; '' if it is none. */
function readerDay(iso) {
  const d = new Date(String(iso || ''));
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * What the Name & place cell says under the name, the place and the origin's
 * code. Every row: the isolate's collection moment (the specimen's, so an
 * intake's too). An intake also says its donor and when it arrived, only to
 * whoever may open the intake (the server sends nulls to anyone else) -- and
 * with two dates on one line, each says which it is. An intake with no
 * original date is dated by its arrival: that day is said once.
 */
function originWhen(item, t) {
  const collected = formatLocalMoment(item.collected_at_local, item.time_known);
  if (!fromIntake(item)) return [collected];
  const received = item.intake?.received_at;
  const sameDay = !!received && String(item.collected_at_local || '').slice(0, 10) === readerDay(received);
  return [
    item.intake?.donor_name,
    received ? <>{t('where.received')} <Day value={received} /></> : null,
    collected && !sameDay ? `${t('where.collected')} ${collected}` : null,
  ];
}

/**
 * A record code the row's ID already begins: "…-P1" for
 * ISO-20261004-0930-SV8WR-001-P1, so the isolate's code is not said again in
 * every chip (the whole code stays in its tooltip). Anything else as it is.
 */
export function ownCode(code, specimenCode) {
  if (!code || !specimenCode) return code || '';
  return code.startsWith(`${specimenCode}-`) ? `…${code.slice(specimenCode.length)}` : code;
}

/** The shown plate's conditions and last check, one line: protocol, target,
 *  what was measured, when it was last checked, the colony and its spores. */
function PlateHint({ plate, t }) {
  const lc = plate.last_check || {};
  // Each value keeps its unit on the right, right to left too.
  const measured = [
    lc.incubation_temp_c != null ? <Ltr>{`${lc.incubation_temp_c} °C`}</Ltr> : null,
    lc.ph != null ? <Ltr>{`pH ${lc.ph}`}</Ltr> : null,
    lc.humidity_pct != null ? <Ltr>{`${lc.humidity_pct}% RH`}</Ltr> : null,
    lc.light_regime ? t(`plates:conditions.light_${lc.light_regime}`) : null,
  ].filter(Boolean);
  const parts = [
    plate.protocol,
    plate.temperature_c != null ? t('lab.target', { temp: plate.temperature_c }) : null,
    measured.length > 0 ? <span data-testid="hub-measured"><Joined parts={measured} /></span> : null,
    lc.date ? <>{t('lab.lastCheck')}: <Day value={lc.date} /></> : null,
    lc.colony_diameter_mm != null ? t('lab.colony', { mm: lc.colony_diameter_mm }) : null,
    lc.sporulation_stage ? conditionsSummary({ sporulation_stage: lc.sporulation_stage }, t) : null,
  ];
  if (!parts.some(Boolean)) return null;
  return <div style={{ ...S.hint, whiteSpace: 'normal' }} data-testid="hub-plate-hint"><Joined parts={parts} /></div>;
}

/**
 * The row's photograph, at the head of its frozen ID cell. Pressed, it opens
 * large (IsolateLightbox) with every photograph of the isolate; its badge
 * counts what the lightbox steps through -- the origin's photographs and its
 * plates' and tubes' -- and its name says both. With no field photograph:
 * the lab's photographs, on the dossier's Photos tab; else the way to its
 * gallery (quickActions.galleryTarget); else an empty frame, so the codes
 * under one another stay lined up.
 */
function PhotoTile({ item, user, t, onLightbox, onPhotosTab, onNavigate }) {
  const id = item.specimen_id;
  const field = item.photo_count || 0;
  const lab = item.lab_photo_count || 0;
  const labWords = lab > 0 ? t('labPhotos', { count: lab }) : null;
  if (item.thumbnail_url) {
    const name = [t('photoAlt', { code: item.specimen_code }), t('photos', { count: field }), labWords]
      .filter(Boolean).join(' · ');
    return (
      <button type="button" className="hub-thumb hub-photo" aria-haspopup="dialog" aria-label={name}
        onClick={(e) => onLightbox(e.currentTarget)} title={t('tip.lightbox')} data-testid={`hub-thumb-${id}`}>
        <img src={resolvePhotoUrl(item.thumbnail_url)} alt={t('photoAlt', { code: item.specimen_code })}
          loading="lazy" width={56} height={42} />
        {field + lab > 1 && <span className="hub-photo__count" aria-hidden="true">{field + lab}</span>}
      </button>
    );
  }
  if (lab > 0) {
    return (
      <button type="button" className="hub-thumb hub-photo hub-photo--empty" aria-label={labWords}
        onClick={onPhotosTab} title={t('tip.labPhotosTab')} data-testid={`hub-lab-photos-${id}`}>
        <span aria-hidden="true">🧫</span>
        <span className="hub-photo__count" aria-hidden="true">{lab}</span>
      </button>
    );
  }
  const target = galleryTarget(item, user);
  if (target) {
    return (
      <button type="button" className="hub-thumb hub-photo hub-photo--empty" aria-label={t('action.gallery')}
        onClick={() => onNavigate(target.path, target.state)} title={t('tip.action.gallery')}
        data-testid={`hub-gallery-${id}`}>
        <span aria-hidden="true">📷</span>
      </button>
    );
  }
  return <span className="hub-photo hub-photo--none" aria-hidden="true" data-testid={`hub-no-photo-${id}`} />;
}

/**
 * A click the browser may still send for a swipe's touch, swallowed for a
 * moment: one landing on the picker the swipe has just opened (on its
 * backdrop it would close it). In the document's capture phase, before React
 * sees it; once, and only within `ms`.
 */
function swallowStrayClick(ms = 400) {
  const until = Date.now() + ms;
  const eat = (e) => {
    document.removeEventListener('click', eat, true);
    if (Date.now() > until) return;
    e.preventDefault();
    e.stopPropagation();
  };
  document.addEventListener('click', eat, true);
  setTimeout(() => document.removeEventListener('click', eat, true), ms);
}

/** Whether the reader asked for less motion; false where it cannot be asked. */
function reducedMotion() {
  try {
    return !!window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
  } catch {
    return false;
  }
}

export default function IsolatesTable({
  view, params, data, onParams, onAction, onNavigate, user, t, dossiers, onStorageSaved, onRefresh, labScope,
  highlightId = null, highlightNonce = 0, onPeek = null, onArchived = null, onThermal = null,
}) {
  const { items = [], total = 0, truncated = false, loading, error, reload } = data;
  const columns = columnsFor(labScope);
  const states = tableStates(view, params.node);
  const page = Math.max(0, Number(params.page) || 0);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const [query, setQuery] = useState(params.q || '');
  const timer = useRef(null);
  // The q this box last wrote to the URL (trimmed), so its own write does not
  // come back into the box and eat the space between two words.
  const wrote = useRef(params.q || '');
  const filtered = !!(params.q || params.state || params.unit || params.origin);
  // The heading names the filter the rows are under: a node's own name, else
  // the view's ("All isolates" with none).
  const heading = params.node ? t(`node.${params.node}.title`) : t(`view.${view}`);

  // The URL is the truth: a back/forward or a node click resets the box.
  useEffect(() => {
    const q = params.q || '';
    if (q === wrote.current) return;
    wrote.current = q;
    setQuery(q);
  }, [params.q]);
  useEffect(() => () => clearTimeout(timer.current), []);

  // A pipeline pressed above (the canvas's isolates view): its row is
  // brought into view and its expand button focused -- on every press, so
  // pressing it again after scrolling away finds it again (the nonce says
  // "again"; the id alone would not change). A press whose row is not shown
  // yet -- an intake just made, whose search is still loading -- waits for
  // the rows: it counts as done only once its row was found.
  const scrolledFor = useRef(0);
  useEffect(() => {
    if (!highlightNonce || !highlightId || scrolledFor.current === highlightNonce) return;
    const row = document.getElementById(`hub-tr-${highlightId}`);
    if (!row) return;
    scrolledFor.current = highlightNonce;
    if (typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'center', behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
    row.querySelector('.hub-expand')?.focus({ preventScroll: true });
  }, [highlightNonce, highlightId, items]);

  // The params as they are when the debounced search fires, not as they were
  // at the keystroke that scheduled it.
  const latest = useRef(params);
  latest.current = params;
  const search = (value) => {
    setQuery(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const q = value.trim();
      wrote.current = q;
      // A search is read best match first unless the reader chose an order
      // (the default, "created", is no choice); clearing it drops the
      // ranking it brought, which means nothing without a query.
      const sort = latest.current.sort || '';
      const patch = { q: q || null, page: null, open: null };
      if (q && (!sort || sort === 'created')) patch.sort = 'relevance';
      else if (!q && sort === 'relevance') patch.sort = null;
      onParams(patch);
    }, 300);
  };

  const toggleOpen = (id) => onParams({ open: params.open === id ? null : id, tab: null });

  // The Lab analytics tray open, if any: one at a time, closed by new rows.
  const [trayFor, setTrayFor] = useState(null);
  useEffect(() => { setTrayFor(null); }, [items]);
  const toolsOf = (item) => trayTools(item, user);
  const shown = useRef(items);
  shown.current = items;
  const [body, setBody] = useState(null);

  // The image source picker: which row, and its row as it was (a refresh keeps it open). Focus goes back
  // to whatever had it when it opened (ui/Dialog), which openPicker sees to.
  const [picker, setPicker] = useState(null);
  const pickerItem = picker ? items.find((i) => i.specimen_id === picker.id) || picker.item : null;
  /** The picker for `item`; focus first on `opener` (the tray's button), else on the card's toggle. */
  const openPicker = (item, opener = null) => {
    const back = opener || document.getElementById(`hub-tray-toggle-${item.specimen_id}`);
    back?.focus?.({ preventScroll: true });
    setPicker({ id: item.specimen_id, item });
  };
  // A card swipe: from rest it may open the picker; a tray the toggle opened, it may close.
  const canReveal = (id) => {
    if (trayFor === id) return true;
    const item = shown.current.find((i) => i.specimen_id === id);
    return !!item && pickerOffered(item, user);
  };
  const onSettle = (id, open, wasOpen) => {
    if (wasOpen) {
      if (!open) setTrayFor((cur) => (cur === id ? null : cur));
      return;
    }
    const item = open ? shown.current.find((i) => i.specimen_id === id) : null;
    if (!item || !pickerOffered(item, user)) return;
    swallowStrayClick();
    setTrayFor(null);
    openPicker(item);
  };
  useCardReveal(body, { openId: trayFor, canReveal, onSettle });
  const toggleTray = (id) => setTrayFor((cur) => (cur === id ? null : id));
  // Escape in a tray: closed, and focus back on its toggle.
  const escapeTray = (item) => {
    setTrayFor(null);
    document.getElementById(`hub-tray-toggle-${item.specimen_id}`)?.focus();
  };
  // A reader who may not use the spore workbench reads the micrographs in the
  // row's own dossier, opened here on its Microscopy tab: the filters and page stay.
  const toDossierTab = (item, tab) => {
    setTrayFor(null);
    onParams({ open: item.specimen_id, hl: item.specimen_id, tab });
  };

  // The photograph opened large: which row, and the button to hand focus
  // back to. The row is read from the current page while it is there, so a
  // refresh of the row reaches the open lightbox.
  const [lightbox, setLightbox] = useState(null);
  const litItem = lightbox ? items.find((i) => i.specimen_id === lightbox.id) || lightbox.item : null;
  const closeLightbox = () => setLightbox(null);

  // Every cell says its column (the stacked view's label) and its role.
  const tdProps = (col) => ({ role: 'cell', 'data-label': t(`columns.${col}`), style: td });

  const actionButton = (item, a) => (
    <button key={a.key} type="button" onClick={() => onAction(a.key, item)}
      style={a.primary ? { ...S.btn(), padding: '4px 10px', fontSize: 13 } : smallBtn}
      title={t(`tip.action.${a.key}`)} data-testid={`hub-action-${a.key}-${item.specimen_id}`}>
      {t(`action.${a.key}`)}
    </button>
  );

  const cell = (col, item) => {
    switch (col) {
      case 'isolateId': {
        const open = params.open === item.specimen_id;
        return (
          <td key={col} {...tdProps(col)} className="hub-sticky">
            <div className="hub-idcell">
              {/* The photograph first, frozen with the code: in view however
                  far the table scrolls. Two buttons side by side, never one
                  inside the other. */}
              <PhotoTile item={item} user={user} t={t} onNavigate={onNavigate}
                onLightbox={(opener) => setLightbox({ id: item.specimen_id, item, opener })}
                onPhotosTab={() => toDossierTab(item, 'photos')} />
              <button type="button" className="hub-expand" aria-expanded={open}
                aria-controls={`hub-row-${item.specimen_id}`} onClick={() => toggleOpen(item.specimen_id)}
                title={open ? t('tip.collapse') : t('tip.expand')} data-testid={`hub-expand-${item.specimen_id}`}>
                <span aria-hidden="true" className="hub-expand__chevron">{open ? '▾' : '▸'}</span>
                {/* The code may wrap at its hyphens where the column is narrow
                    (the frozen column is capped; a phone's card is narrower):
                    the whole code is in the tooltip. */}
                <span title={item.specimen_code} className="hub-expand__code"><Code strong wrap>{item.specimen_code}</Code></span>
              </button>
            </div>
          </td>
        );
      }
      case 'labCode':
        return <td key={col} {...tdProps(col)} data-testid="hub-lab-code"><Code>{item.lab_code}</Code></td>;
      case 'taxon': {
        const latest = item.latest?.identification;
        return (
          <td key={col} {...tdProps(col)}>
            <Taxon name={item.taxon_name} />
            {latest && (
              <div style={S.hint} data-testid="hub-taxon-ident">
                <IdentificationCell latest={latest} t={t} taxonName={item.taxon_name} />
              </div>
            )}
          </td>
        );
      }
      case 'origin': {
        const when = originWhen(item, t);
        const name = (item.given_name || '').trim();
        const place = (item.location_name || '').trim();
        const code = item.origin_code && item.origin_code !== item.specimen_code ? item.origin_code : '';
        if (!name && !place && !code && !when.some(Boolean) && !item.strain_id) {
          return <td key={col} {...tdProps(col)}>—</td>;
        }
        return (
          <td key={col} {...tdProps(col)} className="hub-nameplace">
            {/* The collector's own name for it, then where it was found: each
                isolated (bdi), so it reads in whatever direction it was typed
                while both lines keep the table's alignment; the name cut at
                two lines with the whole of it in the tooltip. */}
            {name && (
              <div className="hub-given-name" title={name} data-testid={`hub-given-name-${item.specimen_id}`}>
                <bdi>{name}</bdi>
              </div>
            )}
            {place && (
              <div className="hub-place" title={t('tip.place')} data-testid={`hub-place-${item.specimen_id}`}>
                <span aria-hidden="true">📍</span> <bdi>{place}</bdi>
              </div>
            )}
            {/* A collection made under the isolate's own code (Field Mode
                mints first) is already named by the ID column. */}
            {code && <div><Code>{code}</Code></div>}
            {when.some(Boolean) && <div style={S.hint}><Joined parts={when} /></div>}
            {item.strain_id && (
              <div>
                <button type="button" className="hub-link" title={t('tip.strain')}
                  onClick={() => onNavigate('/strains', { openStrainId: item.strain_id })}
                  data-testid={`hub-strain-${item.specimen_id}`}>
                  <Code>{item.strain_code || t('bank.strain')}</Code>
                </button>
              </div>
            )}
          </td>
        );
      }
      case 'pipeline':
        return (
          <td key={col} {...tdProps(col)}>
            <PipelineStrip pipeline={item.pipeline} t={t}
              onPress={onPeek ? (stage) => onPeek(item.specimen_id, stage) : null} />
          </td>
        );
      case 'culture': {
        const live = livePlates(item);
        const unstarted = unstartedPlates(item).length;
        const shown = shownPlate(item);
        const finished = (item.plates?.length || 0) > live.length + unstarted;
        return (
          <td key={col} {...tdProps(col)}>
            {live.length > 0 ? (
              <ul className="hub-chips">
                {live.map((p) => (
                  <li key={p.plate_id} className="hub-chip">
                    <span title={t('tip.plateChip', { code: p.plate_code })}>
                      <Code>{ownCode(p.plate_code, item.specimen_code)}</Code> <PlatePill status={p.status} t={t} />
                      {p.days_in_culture != null && <span style={S.hint}> {t('lab.days', { count: p.days_in_culture })}</span>}
                    </span>
                    {/* Under its own chip: the plate "Log check" opens on. */}
                    {p.plate_id === shown?.plate_id && <PlateHint plate={p} t={t} />}
                  </li>
                ))}
              </ul>
            ) : (finished ? t('lab.noLivePlates') : (!unstarted && '—'))}
            {unstarted > 0 && (
              <div style={S.hint} title={t('detail.culture.awaiting_creation')}
                data-testid={`hub-plates-unstarted-${item.specimen_id}`}>
                {t('lab.notStarted', { count: unstarted })}
              </div>
            )}
            {shown && !live.includes(shown) && <PlateHint plate={shown} t={t} />}
          </td>
        );
      }
      case 'bank': {
        const loc = item.bank_location;
        const slants = item.slants || [];
        const place = bankPlace(loc, t) || loc?.storage_location || (slants.length ? t('bank.notStored') : '');
        const statuses = [...new Set(slants.map((s) => s.status))];
        const due = slants.some((s) => s.review_due);
        const method = item.specimen_storage?.preservation_method;
        const copies = item.specimen_storage?.copies_count;
        const dnaTubes = Array.isArray(item.dna_tubes) ? item.dna_tubes.length : (item.dna_tubes || 0);
        const preserved = slants.find((s) => s.preserved_at);
        const how = [
          method ? t(`specimens:storage.methods.${method}`) : (slants.length ? t('specimens:storage.methods.slant') : null),
          slants.length ? t('bank.slantCount', { count: slants.length }) : null,
          copies > 0 ? t('bank.copies', { count: copies }) : null,
          dnaTubes > 0 ? t('bank.dnaTubes', { count: dnaTubes }) : null,
          preserved ? <>{t('bank.preservedOn')} <Day value={preserved.preserved_at} /></> : null,
        ];
        if (!place && !statuses.length && !how.some(Boolean)) return <td key={col} {...tdProps(col)}>—</td>;
        return (
          <td key={col} {...tdProps(col)} data-testid="hub-bank">
            {place && <div>{place}</div>}
            {(statuses.length > 0 || due) && (
              <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                {statuses.map((st) => <SlantPill key={st} status={st} t={t} />)}
                {due && <ReviewDue t={t} />}
              </span>
            )}
            {how.some(Boolean) && <div style={S.hint}><Joined parts={how} /></div>}
          </td>
        );
      }
      case 'actions': {
        const tools = toolsOf(item);
        const trayOpen = trayFor === item.specimen_id;
        return (
          <td key={col} {...tdProps(col)} className="hub-actions-cell">
            <div className="hub-actions">
              {rowActions(item, user, { node: params.node }).map((a) => actionButton(item, a))}
              {/* A redacted row (a student's, seen through an assignment) has no
                  specimen page to open: its workflow and history are not theirs. */}
              {!item.redacted && (
                <button type="button" style={smallBtn} onClick={() => onNavigate(`/specimens/${item.specimen_id}`)}
                  title={t('tip.action.open')} data-testid={`hub-open-${item.specimen_id}`}>
                  {t('action.open')}
                </button>
              )}
              {/* The tray's toggle, last: Tab goes on from it into the tray. The
                  swipe is only the quick way to the same tray (WCAG 2.5.1). */}
              {tools.length > 0 && (
                <button type="button" id={`hub-tray-toggle-${item.specimen_id}`} className="hub-tray-toggle"
                  style={smallBtn} aria-expanded={trayOpen} aria-controls={`hub-tray-${item.specimen_id}`}
                  onClick={() => toggleTray(item.specimen_id)} title={t('tip.tray.toggle')}
                  data-testid={`hub-tray-toggle-${item.specimen_id}`}>
                  {t('tray.toggle')}
                </button>
              )}
            </div>
            {tools.length > 0 && (
              <LabAnalyticsTray item={item} user={user} t={t} open={trayOpen} dossiers={dossiers}
                onThermal={onThermal} onNavigate={onNavigate} onDossierTab={toDossierTab} onEscape={escapeTray}
                onPicker={openPicker} />
            )}
            {/* What a finger's swipe from rest uncovers at the card's physical left (stacked only:
                index.css): the way into the picker, said. Once the tray is open, the tray is. */}
            {pickerOffered(item, user) && (
              <div className="hub-swipe-hint" aria-hidden="true" data-card-reveal={trayOpen ? undefined : ''}
                data-testid={`hub-swipe-hint-${item.specimen_id}`}>
                <span className="hub-swipe-hint__inner">
                  <span className="hub-swipe-hint__icon">🖼️</span>
                  <span>{t('swipe.hint')}</span>
                </span>
              </div>
            )}
          </td>
        );
      }
      default:
        return <td key={col} {...tdProps(col)}>—</td>;
    }
  };

  return (
    <section className="hub-table" aria-labelledby="hub-table-title" data-testid={`hub-table-${view}`}>
      <div className="hub-table__head">
        <h3 id="hub-table-title" style={{ margin: 0, fontSize: 16 }}>{heading}</h3>
        <span style={S.hint} role="status" data-testid="hub-table-count">
          {t('count', { count: total })}{truncated ? ` · ${t('truncated')}` : ''}
        </span>
      </div>

      <div className="hub-toolbar" role="search">
        <label style={{ ...S.label, flex: '3 1 220px' }}>
          {t('toolbar.search')}
          <input type="search" value={query} onChange={(e) => search(e.target.value)} style={S.input}
            placeholder={t('toolbar.searchPlaceholder')} title={t('tip.search')} data-testid="hub-search" />
        </label>
        <label style={{ ...S.label, flex: '1 1 140px' }}>
          {t('toolbar.sort')}
          <select value={params.sort || 'created'} style={S.input} title={t('tip.sort')} data-testid="hub-sort"
            onChange={(e) => onParams({ sort: e.target.value === 'created' ? null : e.target.value, page: null })}>
            {SORTS.map((s) => <option key={s} value={s}>{t(`toolbar.sorts.${s}`)}</option>)}
          </select>
        </label>
        <button type="button" style={S.ghostBtn} data-testid="hub-order"
          onClick={() => onParams({ order: params.order === 'asc' ? null : 'asc', page: null })}
          title={params.order === 'asc' ? t('tip.orderDesc') : t('tip.orderAsc')}>
          {params.order === 'asc' ? t('toolbar.ascending') : t('toolbar.descending')}
        </button>
      </div>

      {(states.length > 0 || params.unit || params.origin) && (
        <div className="hub-filters" role="group" aria-label={t('toolbar.states')}>
          {states.map((s) => {
            const on = params.state === s;
            return (
              <button key={s} type="button" aria-pressed={on} className="hub-filter" title={t('tip.stateChip')}
                onClick={() => onParams({ state: on ? null : s, page: null, open: null })}
                data-testid={`hub-chip-${s}`}>
                {on ? '✓ ' : ''}{t(`states.${chipNode(view, params.node)}.${s}`)}
              </button>
            );
          })}
          {params.unit && (
            <button type="button" aria-pressed className="hub-filter" title={t('tip.clearUnit')}
              onClick={() => onParams({ unit: null, page: null })} data-testid="hub-chip-unit">
              {t(`specimens:storage.units.${params.unit}`)} ✕
            </button>
          )}
          {/* A count opened from the Collection or Intake node keeps only that
              origin's isolates: said here, and removable, never hidden. */}
          {ORIGINS.includes(params.origin) && (
            <button type="button" aria-pressed className="hub-filter" title={t('tip.clearOrigin')}
              onClick={() => onParams({ origin: null, page: null })} data-testid="hub-chip-origin">
              {t(`toolbar.origin.${params.origin}`)} ✕
            </button>
          )}
        </div>
      )}

      {error && (
        <div role="alert" style={{ ...S.error, display: 'flex', gap: 10, alignItems: 'center' }}>
          {error}
          <button type="button" style={smallBtn} onClick={reload} title={t('tip.retry')}>{t('retry')}</button>
        </div>
      )}

      {loading && !items.length && (
        <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />
      )}

      {!loading && !error && items.length === 0 && (
        <OrganicEmptyState kind={EMPTY_KIND[view] || 'generic'} compact={filtered}
          title={filtered ? t('empty.filtered') : t('empty.title')}
          hint={filtered ? t('empty.filteredHint') : t(`empty.hint.${view}`)} />
      )}

      {items.length > 0 && (
        <div className="table-wrap hub-table__wrap" aria-busy={loading || undefined}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="hub-table" role="table">
            <thead role="rowgroup">
              <tr style={{ background: 'var(--c-bg-muted-2)' }} role="row">
                {columns.map((col) => (
                  <th key={col} scope="col" role="columnheader" style={th}
                    className={col === 'isolateId' ? 'hub-sticky' : undefined}>
                    {t(`columns.${col}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody role="rowgroup" ref={setBody}>
              {items.map((item) => {
                const open = params.open === item.specimen_id;
                // The row a pipeline above points at (its aria-controls).
                const lit = !!highlightId && highlightId === item.specimen_id;
                // A row with a tray or the picker is a card the finger may slide (stacked only:
                // index.css, useCardReveal); one without leaves the page's swipe be.
                const card = toolsOf(item).length > 0 || pickerOffered(item, user);
                const classes = [lit && 'hub-row--hl', trayFor === item.specimen_id && 'hub-row--tray']
                  .filter(Boolean).join(' ');
                return (
                  <Fragment key={item.specimen_id}>
                    <tr id={`hub-tr-${item.specimen_id}`} data-testid={`hub-row-${item.specimen_id}`} role="row"
                      className={classes || undefined} aria-current={lit ? 'true' : undefined}
                      data-swipe={card ? 'card' : undefined} data-card-id={card ? item.specimen_id : undefined}
                      style={{ borderTop: '1px solid var(--c-border)' }}>
                      {columns.map((col) => cell(col, item))}
                    </tr>
                    {open && (
                      <tr id={`hub-row-${item.specimen_id}`} className="hub-expanded" role="row">
                        <td colSpan={columns.length} style={{ padding: 0 }} role="cell">
                          <IsolateDetails item={item} entry={dossiers.get(item.specimen_id)}
                            onRetry={() => dossiers.refresh(item.specimen_id)}
                            tab={params.tab} onTab={(tab) => onParams({ tab })} user={user} t={t}
                            onAction={onAction} onNavigate={onNavigate} onStorageSaved={onStorageSaved}
                            onRefresh={onRefresh} onArchived={onArchived} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {litItem && (
        <IsolateLightbox item={litItem} dossiers={dossiers} user={user} t={t} onClose={closeLightbox}
          onNavigate={onNavigate} returnFocusTo={lightbox.opener} />
      )}

      {pickerItem && (
        <ImageSourcePicker item={pickerItem} user={user} t={t} dossiers={dossiers} onNavigate={onNavigate}
          onClose={() => setPicker(null)} />
      )}

      {total > PAGE_SIZE && (
        <nav className="hub-pager" aria-label={t('toolbar.pages')}>
          <button type="button" style={S.ghostBtn} disabled={page <= 0} title={t('tip.prev')}
            onClick={() => onParams({ page: page - 1 || null, open: null })} data-testid="hub-prev">{t('toolbar.prev')}</button>
          <span style={S.hint}>{t('toolbar.page', { page: page + 1, pages })}</span>
          <button type="button" style={S.ghostBtn} disabled={page + 1 >= pages} title={t('tip.next')}
            onClick={() => onParams({ page: page + 1, open: null })} data-testid="hub-next">{t('toolbar.next')}</button>
        </nav>
      )}
    </section>
  );
}

/** Which node's state labels a table's chips use. */
const chipNode = (view, node) => (view === 'bank' ? 'bank' : view === 'intake' ? 'intake'
  : node === 'dna_id' || node === 'to_bank' ? node : 'lab');

const EMPTY_KIND = { all: 'collections', lab: 'plates', bank: 'slants', intake: 'collections', mine: 'collections' };

const th = { padding: '8px 10px', fontSize: 13, fontWeight: 600, textAlign: 'start', whiteSpace: 'nowrap' };
const td = { padding: '8px 10px', fontSize: 14, verticalAlign: 'top' };
const smallBtn = { ...S.ghostBtn, padding: '4px 10px', fontSize: 13 };
